import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Eye, Instagram, Mail, RefreshCw, Star, RotateCw, Tag, Users, BarChart3, Camera, Calendar } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { toast } = useToast();
  const [pendingModels, setPendingModels] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes, enquiriesRes] = await Promise.all([
        axios.get(`${API}/models/pending/list`),
        axios.get(`${API}/admin/all-models`),
        axios.get(`${API}/admin/model-enquiries`)
      ]);
      setPendingModels(pendingRes.data);
      setAllModels(allRes.data);
      setEnquiries(enquiriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch admin data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (modelId, modelName) => {
    try {
      await axios.patch(`${API}/models/${modelId}/approve`);
      toast({
        title: "Model Approved",
        description: `${modelName} has been approved and is now visible in the directory.`,
      });
      fetchData();
    } catch (error) {
      console.error('Error approving model:', error);
      toast({
        title: "Error",
        description: "Failed to approve model.",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (modelId, modelName) => {
    try {
      await axios.patch(`${API}/models/${modelId}/reject`);
      toast({
        title: "Model Rejected",
        description: `${modelName}'s application has been rejected.`,
      });
      fetchData();
    } catch (error) {
      console.error('Error rejecting model:', error);
      toast({
        title: "Error",
        description: "Failed to reject model.",
        variant: "destructive",
      });
    }
  };

  const handleFeature = async (modelId, modelName) => {
    try {
      await axios.post(`${API}/admin/models/${modelId}/feature?duration_days=7`);
      toast({
        title: "Model Featured",
        description: `${modelName} is now featured for 7 days.`,
      });
      fetchData();
    } catch (error) {
      console.error('Error featuring model:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to feature model.",
        variant: "destructive",
      });
    }
  };

  const handleUnfeature = async (modelId, modelName) => {
    try {
      await axios.delete(`${API}/admin/models/${modelId}/feature`);
      toast({
        title: "Feature Removed",
        description: `${modelName} is no longer featured.`,
      });
      fetchData();
    } catch (error) {
      console.error('Error unfeaturing model:', error);
      toast({
        title: "Error",
        description: "Failed to remove feature.",
        variant: "destructive",
      });
    }
  };

  const handleRotateFeatured = async () => {
    try {
      const response = await axios.post(`${API}/admin/rotate-featured`);
      toast({
        title: "Featured Models Rotated",
        description: response.data.message,
      });
      fetchData();
    } catch (error) {
      console.error('Error rotating featured:', error);
      toast({
        title: "Error",
        description: "Failed to rotate featured models.",
        variant: "destructive",
      });
    }
  };

  const approvedModels = allModels.filter(m => m.status === 'approved');
  const featuredModels = approvedModels.filter(m => m.is_featured);

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-red-500 uppercase">
            Admin Panel
          </h1>
          <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-end">
            <Link
              to="/ews-admin-panel/professionals"
              className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="professionals-link"
            >
              <Camera size={18} /> Professionals
            </Link>
            <Link
              to="/ews-admin-panel/bookings"
              className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="bookings-link"
            >
              <Calendar size={18} /> Bookings
            </Link>
            <Link
              to="/ews-admin-panel/analytics"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="analytics-link"
            >
              <BarChart3 size={18} /> Analytics
            </Link>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900 rounded-lg p-4 border border-red-900/20">
            <p className="text-gray-400 text-sm">Pending</p>
            <p className="text-2xl font-bold text-yellow-500">{pendingModels.length}</p>
          </div>
          <div className="bg-zinc-900 rounded-lg p-4 border border-red-900/20">
            <p className="text-gray-400 text-sm">Approved</p>
            <p className="text-2xl font-bold text-green-500">{approvedModels.length}</p>
          </div>
          <div className="bg-zinc-900 rounded-lg p-4 border border-red-900/20">
            <p className="text-gray-400 text-sm">Featured</p>
            <p className="text-2xl font-bold text-yellow-400">{featuredModels.length}</p>
          </div>
          <div className="bg-zinc-900 rounded-lg p-4 border border-red-900/20">
            <p className="text-gray-400 text-sm">Enquiries</p>
            <p className="text-2xl font-bold text-red-500">{enquiries.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-red-900/20 overflow-x-auto">
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-4 px-2 font-mono text-sm tracking-wider uppercase transition-colors whitespace-nowrap ${
              activeTab === 'pending' 
                ? 'text-red-500 border-b-2 border-red-500' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-pending"
          >
            Pending ({pendingModels.length})
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`pb-4 px-2 font-mono text-sm tracking-wider uppercase transition-colors whitespace-nowrap ${
              activeTab === 'approved' 
                ? 'text-red-500 border-b-2 border-red-500' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-approved"
          >
            <Users size={14} className="inline mr-1" /> All Models ({approvedModels.length})
          </button>
          <button
            onClick={() => setActiveTab('enquiries')}
            className={`pb-4 px-2 font-mono text-sm tracking-wider uppercase transition-colors whitespace-nowrap ${
              activeTab === 'enquiries' 
                ? 'text-red-500 border-b-2 border-red-500' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-enquiries"
          >
            Enquiries ({enquiries.length})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Pending Models Tab */}
            {activeTab === 'pending' && (
              <div>
                {pendingModels.length === 0 ? (
                  <div className="text-center py-20 text-gray-400">
                    <p>No pending model applications.</p>
                  </div>
                ) : (
                  <div className="space-y-6" data-testid="pending-models-list">
                    {pendingModels.map((model) => (
                      <div 
                        key={model.id}
                        className="bg-zinc-900 rounded-lg p-6 border border-red-900/20"
                        data-testid={`pending-model-${model.id}`}
                      >
                        <div className="flex flex-col lg:flex-row gap-6">
                          {/* Images */}
                          <div className="flex gap-2 flex-shrink-0">
                            {model.image_urls.slice(0, 4).map((url, idx) => (
                              <div key={idx} className="w-20 h-20 rounded-lg overflow-hidden border border-red-900/30">
                                <img 
                                  src={`${BACKEND_URL}${url}`}
                                  alt={`${model.name} ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-grow">
                            <h3 className="text-xl font-semibold text-white mb-2">{model.name}</h3>
                            
                            <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-3">
                              <span className="flex items-center gap-1">
                                <Mail size={14} /> {model.email}
                              </span>
                              {model.instagram_url && (
                                <a 
                                  href={model.instagram_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 hover:text-red-400 transition-colors"
                                >
                                  <Instagram size={14} /> Instagram
                                </a>
                              )}
                            </div>

                            {/* Categories */}
                            {model.categories && model.categories.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {model.categories.map(cat => (
                                  <span key={cat} className="text-xs bg-zinc-800 text-gray-400 px-2 py-1 rounded flex items-center gap-1">
                                    <Tag size={10} /> {cat}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {model.bio && (
                              <p className="text-gray-500 text-sm mb-4">{model.bio}</p>
                            )}
                            
                            <p className="text-gray-600 text-xs">
                              Applied: {new Date(model.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex lg:flex-col gap-3 flex-shrink-0">
                            <button
                              onClick={() => handleApprove(model.id, model.name)}
                              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                              data-testid={`approve-${model.id}`}
                            >
                              <CheckCircle size={18} /> Approve
                            </button>
                            <button
                              onClick={() => handleReject(model.id, model.name)}
                              className="flex items-center gap-2 bg-zinc-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                              data-testid={`reject-${model.id}`}
                            >
                              <XCircle size={18} /> Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* All Approved Models Tab */}
            {activeTab === 'approved' && (
              <div>
                {/* Rotate Featured Button */}
                <div className="mb-6 flex justify-end">
                  <button
                    onClick={handleRotateFeatured}
                    className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-black px-4 py-2 rounded-lg transition-colors font-semibold"
                  >
                    <RotateCw size={18} /> Rotate Featured Models
                  </button>
                </div>

                {approvedModels.length === 0 ? (
                  <div className="text-center py-20 text-gray-400">
                    <p>No approved models yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {approvedModels.map((model) => (
                      <div 
                        key={model.id}
                        className={`bg-zinc-900 rounded-lg p-4 border transition-all ${
                          model.is_featured 
                            ? 'border-yellow-500/50' 
                            : 'border-red-900/20'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Thumbnail */}
                          <div className="w-16 h-16 rounded-lg overflow-hidden border border-red-900/30 flex-shrink-0">
                            <img 
                              src={model.image_urls[0] ? `${BACKEND_URL}${model.image_urls[0]}` : ''}
                              alt={model.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          {/* Info */}
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-white truncate">{model.name}</h3>
                              {model.is_featured && (
                                <span className="flex items-center gap-1 bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-bold">
                                  <Star size={10} fill="currentColor" /> Featured
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Eye size={12} /> {model.profile_views || 0} views
                              </span>
                              {model.categories?.slice(0, 3).map(cat => (
                                <span key={cat} className="bg-zinc-800 px-2 py-0.5 rounded">{cat}</span>
                              ))}
                            </div>
                          </div>
                          
                          {/* Feature Toggle */}
                          <div className="flex-shrink-0">
                            {model.is_featured ? (
                              <button
                                onClick={() => handleUnfeature(model.id, model.name)}
                                className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                              >
                                <Star size={14} /> Unfeature
                              </button>
                            ) : (
                              <button
                                onClick={() => handleFeature(model.id, model.name)}
                                className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-black px-3 py-2 rounded-lg transition-colors text-sm font-semibold"
                              >
                                <Star size={14} fill="currentColor" /> Feature
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Enquiries Tab */}
            {activeTab === 'enquiries' && (
              <div>
                {enquiries.length === 0 ? (
                  <div className="text-center py-20 text-gray-400">
                    <p>No enquiries yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="enquiries-list">
                    {enquiries.map((enquiry) => (
                      <div 
                        key={enquiry.id}
                        className="bg-zinc-900 rounded-lg p-6 border border-red-900/20"
                        data-testid={`enquiry-${enquiry.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              Enquiry for: {enquiry.model_name}
                            </h3>
                            <p className="text-gray-500 text-sm">
                              {new Date(enquiry.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-mono ${
                            enquiry.status === 'new' 
                              ? 'bg-red-600/20 text-red-400' 
                              : 'bg-green-600/20 text-green-400'
                          }`}>
                            {enquiry.status}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                          <div>
                            <span className="text-gray-500">From:</span>
                            <span className="text-white ml-2">{enquiry.enquirer_name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <a href={`mailto:${enquiry.enquirer_email}`} className="text-red-400 ml-2 hover:text-red-300">
                              {enquiry.enquirer_email}
                            </a>
                          </div>
                        </div>
                        
                        <div className="bg-black/50 rounded-lg p-4">
                          <p className="text-gray-300 text-sm">{enquiry.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
